import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ClubSidebar } from "@/components/ClubSidebar";
import { ClubLayout } from "@/components/ClubLayout";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, lazy, Suspense } from "react";
import { useToast } from "@/hooks/use-toast";

// Lazy load pages for better performance
const NotFound = lazy(() => import("@/pages/not-found"));
const Landing = lazy(() => import("@/pages/landing"));
const LandingRedirect = lazy(() => import("@/pages/landing-redirect"));
const Pricing = lazy(() => import("@/pages/pricing"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Contact = lazy(() => import("@/pages/contact"));
const AuthStart = lazy(() => import("@/pages/auth-start"));
const AuthLogin = lazy(() => import("@/pages/auth-login"));
const AuthLoginMagic = lazy(() => import("@/pages/auth-login-magic"));
const AuthRegisterMagic = lazy(() => import("@/pages/auth-register-magic"));
const AuthCallback = lazy(() => import("@/pages/auth/callback"));
const AuthUserType = lazy(() => import("@/pages/auth-user-type"));
const AuthOrganizerExperience = lazy(() => import("@/pages/auth-organizer-experience"));
const AuthFirstTimeGuide = lazy(() => import("@/pages/auth-first-time-guide"));
const AuthRegister = lazy(() => import("@/pages/auth-register"));
const AuthVerifyEmail = lazy(() => import("@/pages/auth-verify-email"));
const AuthForgotPassword = lazy(() => import("@/pages/auth-forgot-password"));
const AuthResetPassword = lazy(() => import("@/pages/auth-reset-password"));
const PlayerRegistration = lazy(() => import("@/pages/player-registration"));
const Profile = lazy(() => import("@/pages/profile"));
const CreateTournament = lazy(() => import("@/pages/create-tournament"));
const Tournaments = lazy(() => import("@/pages/tournaments"));
const AllTournaments = lazy(() => import("@/pages/all-tournaments"));
const TournamentBracket = lazy(() => import("@/pages/tournament-bracket"));
const TournamentManagement = lazy(() => import("@/pages/tournament-management"));
const TournamentDetails = lazy(() => import("@/pages/tournament-details"));
const Players = lazy(() => import("@/pages/players"));
const MyTournaments = lazy(() => import("@/pages/my-tournaments"));
const Calendar = lazy(() => import("@/pages/calendar"));
const AppmCircuit = lazy(() => import("@/pages/circuitos/appm"));
const AvpCircuit = lazy(() => import("@/pages/circuitos/avp"));
const AppmRanking = lazy(() => import("@/pages/circuitos/appm-ranking"));
const CircuitAdmin = lazy(() => import("@/pages/circuit-admin"));
const JugadorDetail = lazy(() => import("@/pages/jugador-detail"));
const EnVivo = lazy(() => import("@/pages/en-vivo"));
const Reservar = lazy(() => import("@/pages/reservar"));
const MisReservaciones = lazy(() => import("@/pages/mis-reservaciones"));
const ClubRegistro = lazy(() => import("@/pages/club/registro"));
const MisCanchas = lazy(() => import("@/pages/club/mis-canchas"));
const ClubDashboard = lazy(() => import("@/pages/club/dashboard"));
const ClubReservaciones = lazy(() => import("@/pages/club/reservaciones"));
const ClubPerfil = lazy(() => import("@/pages/club/perfil"));
const Leagues = lazy(() => import("@/pages/club/ligas/index"));
const CreateLeague = lazy(() => import("@/pages/club/ligas/nueva"));
const League = lazy(() => import("@/pages/club/ligas/[id]"));
const PublicStandings = lazy(() => import("@/pages/ligas/[id]/posiciones"));
const LeaguePublic = lazy(() => import("@/pages/ligas/[id]/index"));

// Loading component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-bounce-padel">
        <div className="h-12 w-12 rounded-full bg-primary"></div>
      </div>
    </div>
  );
}

// Protected Route wrapper - redirects to login if not authenticated
function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/auth/login');
    }
  }, [isAuthenticated, isLoading, setLocation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-bounce-padel">
          <div className="h-12 w-12 rounded-full bg-primary"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Component />;
}

// Protected Route wrapper for clubs - redirects to login if not authenticated or not club role
function ProtectedRouteClub({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/auth/login');
    } else if (!isLoading && isAuthenticated && user?.role !== 'club') {
      setLocation('/');
    }
  }, [isAuthenticated, isLoading, user, setLocation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-bounce-padel">
          <div className="h-12 w-12 rounded-full bg-primary"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'club') {
    return null;
  }

  return <Component />;
}

// Router with auth protection on private routes
function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/planes" component={Pricing} />
        <Route path="/contacto" component={Contact} />
        <Route path="/auth/start" component={AuthStart} />
        <Route path="/auth/login" component={AuthLogin} />
        <Route path="/auth/login-magic" component={AuthLoginMagic} />
        <Route path="/auth/register-magic" component={AuthRegisterMagic} />
        <Route path="/auth/callback" component={AuthCallback} />
        <Route path="/auth/user-type" component={AuthUserType} />
        <Route path="/auth/organizer-experience" component={AuthOrganizerExperience} />
        <Route path="/auth/first-time-guide" component={AuthFirstTimeGuide} />
        <Route path="/auth/register" component={AuthRegister} />
        <Route path="/auth/verify-email" component={AuthVerifyEmail} />
        <Route path="/auth/forgot-password" component={AuthForgotPassword} />
        <Route path="/auth/reset-password" component={AuthResetPassword} />
        <Route path="/player-registration" component={PlayerRegistration} />
        <Route path="/dashboard" component={() => <ProtectedRoute component={Dashboard} />} />
        <Route path="/torneos/crear" component={() => <ProtectedRoute component={CreateTournament} />} />
        <Route path="/torneos/:id/editar" component={() => <ProtectedRoute component={CreateTournament} />} />
        <Route path="/torneos/:id/cuadro" component={() => <ProtectedRoute component={TournamentBracket} />} />
        <Route path="/torneos/:id/gestionar" component={() => <ProtectedRoute component={TournamentManagement} />} />
        <Route path="/todos-los-torneos" component={AllTournaments} />
        <Route path="/torneos" component={() => <ProtectedRoute component={Tournaments} />} />
        <Route path="/torneos/:id" component={TournamentDetails} />
        <Route path="/circuitos/appm" component={AppmCircuit} />
        <Route path="/circuitos/appm/ranking" component={AppmRanking} />
        <Route path="/circuitos/avp" component={AvpCircuit} />
        <Route path="/circuitos/:id/admin" component={() => <ProtectedRoute component={CircuitAdmin} />} />
        <Route path="/en-vivo" component={EnVivo} />
        <Route path="/jugadores/:id" component={JugadorDetail} />
        <Route path="/reservar" component={Reservar} />
        <Route path="/ligas/:id" component={LeaguePublic} />
        <Route path="/ligas/:id/posiciones" component={PublicStandings} />
        <Route path="/mis-reservaciones" component={() => <ProtectedRoute component={MisReservaciones} />} />
        <Route path="/club/registro" component={ClubRegistro} />
        <Route path="/ligas" component={() => <ProtectedRoute component={() => <div className="p-8"><h1 className="text-2xl font-heading font-bold">Ligas</h1><p className="text-muted-foreground mt-2">Gestión de ligas próximamente...</p></div>} />} />
        <Route path="/jugadores" component={() => <ProtectedRoute component={Players} />} />
        <Route path="/ranking" component={() => <ProtectedRoute component={() => <div className="p-8"><h1 className="text-2xl font-heading font-bold">Ranking</h1><p className="text-muted-foreground mt-2">Ranking de jugadores próximamente...</p></div>} />} />
        <Route path="/perfil" component={() => <ProtectedRoute component={Profile} />} />
        <Route path="/mis-torneos" component={() => <ProtectedRoute component={MyTournaments} />} />
        <Route path="/calendario" component={() => <ProtectedRoute component={Calendar} />} />
        <Route path="/mis-ligas" component={() => <ProtectedRoute component={() => <div className="p-8"><h1 className="text-2xl font-heading font-bold">Mis Ligas</h1><p className="text-muted-foreground mt-2">Tus ligas próximamente...</p></div>} />} />
        <Route path="/reportes" component={() => <ProtectedRoute component={() => <div className="p-8"><h1 className="text-2xl font-heading font-bold">Reportes</h1><p className="text-muted-foreground mt-2">Reportes y analytics próximamente...</p></div>} />} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// Club router with isolated layout
function ClubRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/club/dashboard" component={() => <ProtectedRouteClub component={ClubDashboard} />} />
        <Route path="/club/reservaciones" component={() => <ProtectedRouteClub component={ClubReservaciones} />} />
        <Route path="/club/perfil" component={() => <ProtectedRouteClub component={ClubPerfil} />} />
        <Route path="/club/mis-canchas" component={() => <ProtectedRouteClub component={MisCanchas} />} />
        <Route path="/club/ligas" component={() => <ProtectedRouteClub component={Leagues} />} />
        <Route path="/club/ligas/nueva" component={() => <ProtectedRouteClub component={CreateLeague} />} />
        <Route path="/club/ligas/:id" component={() => <ProtectedRouteClub component={League} />} />
      </Switch>
    </Suspense>
  );
}

// Inner component that uses useAuth - must be inside QueryClientProvider
function AppContent() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [location] = useLocation();
  
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  // Public routes that don't require authentication
  const publicRoutes = ['/', '/planes', '/contacto', '/reservar', '/club/dashboard', '/auth/start', '/auth/login', '/auth/login-magic', '/auth/register-magic', '/auth/callback', '/auth/user-type', '/auth/organizer-experience', '/auth/first-time-guide', '/auth/register', '/auth/verify-email', '/auth/forgot-password', '/auth/reset-password', '/player-registration', '/circuitos/appm', '/circuitos/appm/ranking', '/circuitos/avp', '/en-vivo'];
  const isPublicRoute = publicRoutes.includes(location) || location.startsWith('/jugadores/') || location.startsWith('/club/') || location.startsWith('/club/mis-canchas');

  // Only show loader on authenticated pages while checking auth status
  const shouldShowLoader = isLoading && !isPublicRoute;

  if (shouldShowLoader) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-bounce-padel">
          <div className="h-12 w-12 rounded-full bg-primary"></div>
        </div>
      </div>
    );
  }

  // Show content without sidebar for public routes when NOT authenticated
  if (!isAuthenticated && isPublicRoute) {
    return <Router />;
  }

  // If not authenticated and trying to access protected route, show router (ProtectedRoute will handle redirect)
  if (!isAuthenticated) {
    return <Router />;
  }

  // Players should see the landing without sidebar
  if (user?.role === 'player') {
    return <Router />;
  }

  // Clubs should see the club layout with isolated router
  if (user?.role === 'club') {
    return <ClubLayout><ClubRouter /></ClubLayout>;
  }

  // Show content with sidebar for authenticated organizers only
  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center gap-4 p-4 border-b border-border bg-background">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex-1"></div>
          </header>
          <main className="flex-1 min-w-0 overflow-auto p-6">
            <Router />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

// Main App component wraps everything with providers
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AppContent />
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
